import { useMemo } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { CircularLoader, Tooltip } from "@similarweb/ui-components";

import { loaderOptions } from "app/pages/activation/configs";
import Chart from "pro-features/components/Chart/src/Chart";
import CoreTrendCell from "pro-features/components/core cells/src/CoreTrendCell/CoreTrendCell";
import { translate } from "app/filters/i18nFilter";
import { gaWidgetDataAutomation } from "app/pages/activation/components/GAWidget/dataAutomationKeys";
import { GraphChange } from "app/pages/activation/components/GAWidget/Widget/GraphChange";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";

import type { GAGraphData } from "../../../types/api";
import type { BenchmarkGranularity } from "../../../types/common";
import { GAWidgetTexts } from "../texts";
import type { ConfigSchema, PieChartDataSchema } from "../types";
import { WidgetType } from "../types";
import { METRIC_NAMES } from "../widgetsConfig";

import { defaultEmptyDataGraph, defaultEmptyPieData, getBarChart, getPieChartConfig, gradient } from "./config";
import * as SC from "./styles";

type WidgetSchema = {
    isConnected: boolean;
    isDragging?: boolean;
    isSwSource?: boolean;
    data: GAGraphData | PieChartDataSchema;
    granularity: BenchmarkGranularity;
    isLoading: boolean;
    metricName: string;
    withBorder?: boolean;
    onClick?: () => void;
};

const Widget = ({
    title,
    icon,
    tooltipText,
    type,
    valueFormatter,
    changeFormatter,
    isConnected,
    isSwSource,
    data,
    granularity,
    isLoading,
    metricName,
    withBorder = true,
    onClick,
}: ConfigSchema & WidgetSchema) => {
    const graphData = data as GAGraphData;
    const pieData = data as PieChartDataSchema;

    const isGraphWidget = type === WidgetType.Graph;

    const pieChartConfig = useMemo(() => getPieChartConfig(isConnected), [isConnected]);
    const barChartConfig = useMemo(
        () => getBarChart(isConnected, granularity, valueFormatter),
        [granularity, isConnected, valueFormatter],
    );

    if (isLoading) {
        return (
            <SC.GraphContainer
                withBorder={withBorder}
                isLoading={isLoading}
            >
                <SC.LoaderContainer>
                    <CircularLoader options={loaderOptions} />
                </SC.LoaderContainer>
            </SC.GraphContainer>
        );
    }

    return (
        <SC.GraphContainer
            data-automation={gaWidgetDataAutomation.widgetItem}
            withBorder={withBorder}
            onClick={onClick}
        >
            <div>
                <SC.MetricHeader>
                    <SWReactIcons
                        iconName={icon}
                        size="xs"
                    />

                    <SC.MetricTitle>{title}</SC.MetricTitle>

                    <Tooltip
                        enabled={!!tooltipText}
                        content={translate(tooltipText)}
                    >
                        <span>
                            <SWReactIcons
                                iconName="info"
                                size="xs"
                            />
                        </span>
                    </Tooltip>
                </SC.MetricHeader>

                {isGraphWidget ? (
                    <GraphChange
                        isConnected={isConnected}
                        isSwSource={isSwSource}
                        valueFormatter={valueFormatter}
                        metricName={metricName}
                        graphData={graphData}
                        changeFormatter={changeFormatter}
                        granularity={granularity}
                        isInverted={metricName === METRIC_NAMES.BOUNCE_RATE}
                    />
                ) : (
                    <SC.PieContainer>
                        <SC.PieValueContainer>
                            <SC.PieValueWrapper>
                                <FlexRow>
                                    <SC.Marker backgroundColor={colorsPalettes.blue[400]} />
                                    <SC.PieValueName>{translate(GAWidgetTexts.distributionDesktop)}</SC.PieValueName>
                                </FlexRow>

                                <SC.PieValue>{isConnected ? valueFormatter(pieData?.desktop) : "-"}</SC.PieValue>
                            </SC.PieValueWrapper>

                            <FlexRow>
                                <SC.Marker backgroundColor={colorsPalettes.blue[200]} />
                                <SC.PieValueName>{translate(GAWidgetTexts.distributionMobile)}</SC.PieValueName>
                            </FlexRow>

                            <SC.PieValue>{isConnected ? valueFormatter(pieData?.mobile) : "-"}</SC.PieValue>
                        </SC.PieValueContainer>

                        <Chart
                            domProps={{ style: { marginTop: "26px" } }}
                            type={chartTypes.PIE}
                            data={
                                isConnected && pieData?.chartData && !pieData?.isEmpty
                                    ? [pieData?.chartData]
                                    : defaultEmptyPieData
                            }
                            config={pieChartConfig}
                        />
                    </SC.PieContainer>
                )}
            </div>

            <div>
                {isGraphWidget ? (
                    <CoreTrendCell
                        data={isConnected ? [{ data: graphData?.trend }] : [defaultEmptyDataGraph]}
                        config={barChartConfig}
                        params={gradient}
                    />
                ) : null}
            </div>
        </SC.GraphContainer>
    );
};

export default Widget;
